/**
 * waterDropSound.js — synthesized droplet audio.
 *
 * Uses Web Audio API to generate a soft "plink" (400Hz → 200Hz sweep with
 * decaying envelope). Zero asset dependency, fires once per event.
 *
 * Respects the site-wide music volume slider stored in localStorage("bes_v").
 * Only plays if the user has already enabled audio (bes_v > 0) — so we never
 * play unsolicited sound.
 */

let ctx = null;

function getCtx() {
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
    } catch (e) {
      return null;
    }
  }
  // Resume if suspended (some browsers auto-suspend until user gesture)
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

/**
 * Play a soft water-drop sound.
 * @param {Object} [opts]
 * @param {number} [opts.pitch=1] — pitch multiplier (0.5..2)
 * @param {number} [opts.volume=0.35] — additional gain multiplier (0..1)
 */
export function playDrop({ pitch = 1, volume = 0.35 } = {}) {
  // Respect user preference — only play if music is on
  const globalVol = parseFloat(localStorage.getItem("bes_v") || "0");
  if (isNaN(globalVol) || globalVol <= 0) return;

  const c = getCtx();
  if (!c) return;

  const now = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  const filter = c.createBiquadFilter();

  osc.type = "sine";
  osc.frequency.setValueAtTime(880 * pitch, now);
  osc.frequency.exponentialRampToValueAtTime(220 * pitch, now + 0.28);

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(2400, now);
  filter.Q.value = 3;

  const finalVol = Math.min(1, globalVol * volume);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(finalVol, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(c.destination);

  osc.start(now);
  osc.stop(now + 0.5);
}
